'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabaseClient';

interface FormData {
  name: string;
  email: string;
  phone: string;
  userType: string;
  urgency: string;
  subject: string;
  message: string;
  preferredContact: string;
  bestTime: string;
}

interface ContactFormsProps {
  className?: string;
}

const ContactForms = ({ className = '' }: ContactFormsProps) => {
  const [activeForm, setActiveForm] = useState<'general' | 'seller' | 'buyer' | 'referral'>(
    'general'
  );
  const initialFormData: FormData = {
    name: '',
    email: '',
    phone: '',
    userType: '',
    urgency: 'normal',
    subject: '',
    message: '',
    preferredContact: 'email',
    bestTime: 'anytime',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const formTabs = [
    { id: 'general', label: 'General Inquiry', icon: 'ChatBubbleLeftRightIcon' },
    { id: 'seller', label: 'I Want to Sell', icon: 'HomeIcon' },
    { id: 'buyer', label: 'I Want to Buy', icon: 'CurrencyDollarIcon' },
    { id: 'referral', label: 'Professional Referral', icon: 'UserGroupIcon' },
  ];

  const urgencyLevels = [
    { value: 'emergency', label: 'Emergency (Foreclosure/Urgent)', color: 'text-error' },
    { value: 'high', label: 'High Priority (Within 24 hours)', color: 'text-warning' },
    { value: 'normal', label: 'Normal (Within 2-3 days)', color: 'text-success' },
    { value: 'low', label: 'Low Priority (Within a week)', color: 'text-muted-foreground' },
  ];

  const emptyToNull = (value: string) => {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSubmitted(false);

    try {
      const contactPayload = {
        form_type: activeForm,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        urgency: emptyToNull(formData.urgency),
        subject: emptyToNull(formData.subject),
        message: formData.message.trim(),
        preferred_contact: emptyToNull(formData.preferredContact),
        best_time: emptyToNull(formData.bestTime),
      };

      const { error: submitError } = await supabase.from('contact_messages').insert(contactPayload);

      if (submitError) {
        throw submitError;
      }

      if (activeForm === 'buyer') {
        const buyerNotes = [
          contactPayload.subject ? `Subject: ${contactPayload.subject}` : null,
          `Message: ${contactPayload.message}`,
          contactPayload.preferred_contact
            ? `Preferred contact: ${contactPayload.preferred_contact}`
            : null,
          contactPayload.best_time ? `Best time: ${contactPayload.best_time}` : null,
          contactPayload.urgency ? `Priority: ${contactPayload.urgency}` : null,
          'Submitted from the Contact page buyer tab.',
        ]
          .filter(Boolean)
          .join('\n');

        const { error: buyerLeadError } = await supabase.from('buyer_leads').insert({
          full_name: contactPayload.name,
          email: contactPayload.email,
          phone: emptyToNull(contactPayload.phone),
          strategy: 'Contact page buyer inquiry',
          notes: buyerNotes,
        });

        if (buyerLeadError) {
          throw buyerLeadError;
        }
      }

      setSubmitted(true);
      setFormData(initialFormData);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      console.error('Error submitting contact form:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const getFormTitle = () => {
    switch (activeForm) {
      case 'seller':
        return 'Property Submission Form';
      case 'buyer':
        return 'Investor Application Form';
      case 'referral':
        return 'Professional Referral Form';
      default:
        return 'General Contact Form';
    }
  };

  const getFormDescription = () => {
    switch (activeForm) {
      case 'seller':
        return "Tell us about your property and situation. We'll provide a fair cash offer within 24 hours.";
      case 'buyer':
        return 'Join our investor network to receive exclusive deal notifications and investment opportunities.';
      case 'referral':
        return 'Partner with us as a referring professional. We value long-term relationships with industry experts.';
      default:
        return "Have questions about our services? We're here to help with any inquiries you may have.";
    }
  };

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the form that best matches your needs for a personalized response.
          </p>
        </div>

        {/* Form Tabs */}
        <div className="flex flex-wrap justify-center mb-8 border-b border-border">
          {formTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveForm(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-3 font-medium silk-smooth border-b-2 ${
                activeForm === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon as any} size={20} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Form Container */}
        <div className="bg-card rounded-lg border border-border p-6 lg:p-8 luxury-shadow">
          <div className="mb-6">
            <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
              {getFormTitle()}
            </h3>
            <p className="text-muted-foreground">{getFormDescription()}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {submitted && (
              <div className="rounded-md border border-success/30 bg-success/10 p-4 text-sm text-success">
                Thank you. Your information was sent successfully.
              </div>
            )}

            {error && (
              <div className="rounded-md border border-error/30 bg-error/10 p-4 text-sm text-error">
                {error}
              </div>
            )}
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                  placeholder="Your phone number"
                />
              </div>

              <div>
                <label htmlFor="urgency" className="block text-sm font-medium text-foreground mb-2">
                  Priority Level
                </label>
                <select
                  id="urgency"
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                >
                  {urgencyLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Preferences */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="preferredContact"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Preferred Contact Method
                </label>
                <select
                  id="preferredContact"
                  name="preferredContact"
                  value={formData.preferredContact}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone Call</option>
                  <option value="text">Text Message</option>
                  <option value="any">Any Method</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="bestTime"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Best Time to Contact
                </label>
                <select
                  id="bestTime"
                  name="bestTime"
                  value={formData.bestTime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                >
                  <option value="anytime">Anytime</option>
                  <option value="morning">Morning (8AM-12PM)</option>
                  <option value="afternoon">Afternoon (12PM-5PM)</option>
                  <option value="evening">Evening (5PM-8PM)</option>
                  <option value="weekend">Weekends Only</option>
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth"
                placeholder="Brief description of your inquiry"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent silk-smooth resize-vertical"
                placeholder={
                  activeForm === 'seller'
                    ? 'Tell us about your property, situation, and timeline...'
                    : activeForm === 'buyer'
                      ? 'Describe your investment criteria, budget, and preferred areas...'
                      : activeForm === 'referral'
                        ? 'Tell us about your professional background and referral interests...'
                        : 'Please provide details about your inquiry...'
                }
              />
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium silk-smooth hover:bg-primary/90 shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="PaperAirplaneIcon" size={20} className="inline mr-2" />
                {loading
                  ? 'Sending...'
                  : activeForm === 'buyer'
                    ? 'Join Buyers List'
                    : 'Send Message'}
              </button>

              {formData.urgency === 'emergency' && (
                <button
                  type="button"
                  className="bg-error text-error-foreground px-6 py-3 rounded-md font-medium silk-smooth hover:bg-error/90 trust-glow"
                >
                  <Icon name="PhoneIcon" size={20} className="inline mr-2" />
                  Call Emergency Line
                </button>
              )}
            </div>

            {/* Response Time Notice */}
            <div className="bg-muted p-4 rounded-md">
              <div className="flex items-start space-x-3">
                <Icon name="InformationCircleIcon" size={20} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">
                    Response Time Commitment
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formData.urgency === 'emergency' && 'Emergency situations: Within 1 hour'}
                    {formData.urgency === 'high' &&
                      'High priority: Within 4 hours during business days'}
                    {formData.urgency === 'normal' && 'Normal priority: Within 24 hours'}
                    {formData.urgency === 'low' && 'Low priority: Within 2-3 business days'}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForms;
